import React, { FormEventHandler, useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { Link as RouterLink } from '~/lib/react-router-dom';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';
import UserSearchWidget, { SearchResult } from '~/components/Common/UserSearchWidget';

import { useCreateDiscountVoucher } from '~/hooks/apis/agent/useCreateDiscountVoucher';
import useCurrentBrand from '~/hooks/useCurrentBrand';

import { CreateDiscountVoucherParams } from '~/types/services/agentHub';

const MAX_USERS = 100;
const USER_SEARCH_FIELD_ID = 'agent-voucher-user-search';

type SelectedUser = {
  id: string;
  email: string;
};

function AgentVouchers() {
  const { enqueueSnackbar } = useSnackbar();
  const brand = useCurrentBrand();
  const createDiscountVoucherMutation = useCreateDiscountVoucher();

  // Voucher form fields
  const [voucherTitle, setVoucherTitle] = useState('');
  const [rewardAmount, setRewardAmount] = useState('');
  const [minSpend, setMinSpend] = useState('');

  // User search / selection
  const [userSearchValue, setUserSearchValue] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<Array<SelectedUser>>([]);

  const selectedIds = useMemo(() => new Set(selectedUsers.map((u) => u.id)), [selectedUsers]);

  const reachedMax = selectedUsers.length >= MAX_USERS;

  const handleSelectUser = useCallback(
    (result: SearchResult | null) => {
      if (result?.entityType !== 'user') {
        return;
      }

      const id = result.value;
      if (!id) {
        return;
      }

      if (selectedIds.has(id)) {
        enqueueSnackbar('User is already in the list', { variant: 'info' });
        setUserSearchValue('');
        return;
      }

      if (selectedUsers.length >= MAX_USERS) {
        enqueueSnackbar(`You can add up to ${MAX_USERS} users`, { variant: 'warning' });
        return;
      }

      setSelectedUsers((prev) => [...prev, { id, email: result.email ?? '' }]);
      // Clear the field so the agent can search for the next user.
      // Deferred so it lands after UserSearchWidget sets the selected label.
      setTimeout(() => setUserSearchValue(''), 0);
    },
    [enqueueSnackbar, selectedIds, selectedUsers.length],
  );

  const removeUser = useCallback((id: string) => {
    setSelectedUsers((prev) => prev.filter((u) => u.id !== id));
  }, []);

  const canSubmit =
    voucherTitle.trim().length > 0 &&
    rewardAmount.trim().length > 0 &&
    minSpend.trim().length > 0 &&
    selectedUsers.length > 0;

  const resetForm = useCallback(() => {
    setVoucherTitle('');
    setRewardAmount('');
    setMinSpend('');
    setSelectedUsers([]);
    setUserSearchValue('');
  }, []);

  const handleVoucherSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();

    const reward = Number(rewardAmount);
    const spend = Number(minSpend);
    if (!Number.isFinite(reward) || reward <= 0 || !Number.isFinite(spend) || spend < 0) {
      enqueueSnackbar('Please enter a reward greater than 0 and a non-negative minimum spend', {
        variant: 'warning',
      });
      return;
    }

    const payload: CreateDiscountVoucherParams = {
      voucherTitle: voucherTitle.trim(),
      rewardAmount: reward,
      minSpend: spend,
      userIds: selectedUsers.map((u) => u.id),
      brand: brand as CreateDiscountVoucherParams['brand'],
    };

    createDiscountVoucherMutation.mutate(payload, {
      onSuccess: () => {
        enqueueSnackbar('Your request is being processed', { variant: 'success' });
        resetForm();
      },
      onError: (error) => {
        enqueueSnackbar(`Failed to create voucher: ${(error as Error).message}`, { variant: 'error' });
      },
    });
  };

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Agent Vouchers</title>
      </Helmet>

      <Box component="form" onSubmit={handleVoucherSubmit} mt={2}>
        <PageSubheader title="Voucher details" />

        <Typography variant="body2" color="text.secondary" mt={1} maxWidth={640}>
          This voucher applies to all product types, with the minimum spend as its only condition. To set any other
          restrictions, create or edit the voucher from the{' '}
          <Link component={RouterLink} to="/marketing/vouchers">
            Vouchers page
          </Link>{' '}
          instead.
        </Typography>

        <Stack spacing={2} mt={2} maxWidth={480}>
          <TextField
            label="Voucher title"
            value={voucherTitle}
            onChange={(e) => setVoucherTitle(e.target.value)}
            required
            fullWidth
          />

          <TextField
            label="Reward amount"
            type="number"
            value={rewardAmount}
            onChange={(e) => setRewardAmount(e.target.value)}
            required
            fullWidth
            slotProps={{
              input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
              htmlInput: { min: 1 },
            }}
          />

          <TextField
            label="Minimum spend"
            type="number"
            value={minSpend}
            onChange={(e) => setMinSpend(e.target.value)}
            required
            fullWidth
            slotProps={{
              input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
              htmlInput: { min: 0 },
            }}
          />
        </Stack>

        <Box mt={4}>
          <PageSubheader title="Select customers" />

          <Box mt={2} maxWidth={480}>
            <UserSearchWidget
              id={USER_SEARCH_FIELD_ID}
              label="Name / Email / Customer Support Code / User ID"
              formErrors={[]}
              onChange={() => {
                /* selection handled via onSelectResult */
              }}
              onSelectResult={handleSelectUser}
              passedAutocompleteValue={userSearchValue}
              setPassedAutocompleteValue={setUserSearchValue}
              disabled={reachedMax}
            />

            <Typography variant="caption" color="text.secondary" mt={1} display="block">
              {selectedUsers.length}/{MAX_USERS} users added
              {reachedMax ? ' — remove a user to add another.' : ''}
            </Typography>
          </Box>

          {selectedUsers.length > 0 && (
            <List dense sx={{ maxWidth: 480 }}>
              {selectedUsers.map((user) => (
                <ListItem
                  key={user.id}
                  secondaryAction={
                    <IconButton edge="end" aria-label="Remove user" onClick={() => removeUser(user.id)}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  }
                >
                  <ListItemText primary={user.email || '—'} secondary={user.id} />
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        <Box mt={4}>
          <Typography variant="body2" color="text.secondary" maxWidth={640}>
            Once processed, the voucher will be available on the{' '}
            <Link component={RouterLink} to="/marketing/vouchers">
              Vouchers page
            </Link>
            .
          </Typography>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={!canSubmit || createDiscountVoucherMutation.isPending}
            sx={{ mt: 2 }}
          >
            {createDiscountVoucherMutation.isPending ? 'Creating…' : 'Create voucher'}
          </Button>
        </Box>
      </Box>
    </Container>
  );
}

export default AgentVouchers;
